import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { useTransaction } from '@/features/transactions/hooks'
import { SourceDestinationPanel } from '@/features/transactions/components/SourceDestinationPanel'
import { BalancePanel } from '@/features/transactions/components/BalancePanel'
import { StatusChip } from '@/features/transactions/components/StatusChip'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { formatNaira, formatDateTime } from '@/lib/format'

interface Props {
  transactionId: string | null
}

/**
 * AF-126 — "What" section. Reuses the AF-125 transaction panels so the alert
 * detail and the transaction detail render identically; analysts learn one
 * pattern, applies everywhere.
 */
export function WhatSection({ transactionId }: Props) {
  if (!transactionId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Linked transaction</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-fg">
            This alert is customer-level — no single transaction to link.
          </p>
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked transaction</CardTitle>
      </CardHeader>
      <CardContent>
        <TxnContent transactionId={transactionId} />
      </CardContent>
    </Card>
  )
}

function TxnContent({ transactionId }: { transactionId: string }) {
  const { data: txn, isLoading, isError } = useTransaction(transactionId)
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (isError || !txn) {
    return <p className="text-sm text-muted-fg">Transaction unavailable.</p>
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Link
          to={`/transactions/${txn.id}`}
          className="flex items-center gap-1 font-mono text-xs text-primary hover:underline"
        >
          {txn.transaction_ref}
          <ExternalLink size={10} />
        </Link>
        <StatusChip status={txn.status} />
        <ChannelBadge channel={txn.channel} />
        <span className="font-mono tabular-nums">{formatNaira(txn.amount_minor)}</span>
        <span className="text-xs text-muted-fg">{formatDateTime(txn.occurred_at)}</span>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <SourceDestinationPanel side={txn.source} title="Source" />
        <SourceDestinationPanel side={txn.destination} title="Destination" />
      </div>
      <BalancePanel balances={txn.balances} />
    </div>
  )
}
